import { toast } from "react-toastify";
import Button from "../../components/Button";
import { withTranslation } from "react-i18next";
import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux/es/exports";
import { MdOutlineDataSaverOff } from "react-icons/md";
import { useNavigate, useParams } from "react-router-dom";
import ToggleWithLabel from "../../components/ToggleWithLabel";
import { CountryGuideModal } from "./CountryGuideModal";
import CountryProviders from "./CountryProviders";

const AddAndEditCountry = ({ t }) => {
  const { token } = useSelector((state) => state.user);
  const navigate = useNavigate();
  const { id } = useParams();

  const [form, setForm] = useState({
    name: "",
    code: "",
    length: 0,
    codeSlice: "",
    phoneCode: "",
    phoneFormat: "",
    digitsLength: 0,
    currencyId: null,
  });

  const [isActive, setIsActive] = useState(false);
  const [isDefault, setIsDefault] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currencies, setCurrencies] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_HOST_BASE_URL}/v1/currency/list`,
          {
            method: "GET",
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          }
        );
        if (response.ok) {
          const data = await response.json();
          setCurrencies(data);
        }
      } catch (err) {
        console.error("Currency fetch error:", err);
      }
    })();
  }, [token]);

  useEffect(() => {
    if (!id) {
      setIsModalOpen(true);
    }
  }, [id]);

  useEffect(() => {
    if (id) {
      (async () => {
        try {
          const response = await fetch(
            `${process.env.REACT_APP_HOST_BASE_URL}/v1/country/${id}`,
            {
              method: "GET",
              headers: {
                Authorization: `Bearer ${token}`,
                "Content-Type": "application/json",
              },
            }
          );
          if (response.ok) {
            const data = await response.json();
            setForm({
              id: data.id,
              name: data.name || "",
              code: data.code || "",
              phoneCode: data.phoneCode || "",
              digitsLength: data.digitsLength || 0,
              phoneFormat: data.phoneFormat || "",
              length: data.length || 0,
              codeSlice: data.codeSlice || "",
              currencyId: data.currencyId || null,
            });
            setIsActive(data.isActive);
            setIsDefault(data.isDefault);
          }
        } catch (err) {
          console.error("Fetch error:", err);
        }
      })();
    }
  }, [id, token]);

  const handleSetIsActive = (value) => {
    setIsActive(value);
    setForm((prev) => ({ ...prev, isActive: value }));
  };

  const handleSetIsDefault = (value) => {
    setIsDefault(value);
    setForm((prev) => ({ ...prev, isDefault: value }));
  };

  const handleSubmit = async (evt) => {
    setIsLoading(true);
    evt.preventDefault();

    let params = {
      name: form.name,
      code: form.code,
      isActive: isActive,
      length: form.length,
      isDefault: isDefault,
      phoneCode: form.phoneCode,
      codeSlice: form.codeSlice,
      phoneFormat: form.phoneFormat,
      digitsLength: form.digitsLength,
      currencyId: form.currencyId ? parseInt(form.currencyId) : null,
    };

    if (id) {
      params.id = parseInt(id);
    }

    const file = form.file;

    try {
      const json = JSON.stringify(params);
      const blob = new Blob([json], { type: "application/json" });
      const formData = new FormData();
      formData.append("country", blob);
      if (file) {
        formData.append("file", file);
      }

      const url = `${process.env.REACT_APP_HOST_BASE_URL}/v1/country`;

      const response = await fetch(url, {
        method: id ? "PUT" : "POST",
        headers: {
          Authorization: `Bearer ${token}`,
        },
        body: formData,
      });

      if (response.ok) {
        navigate("/country");
        toast.success(
          id
            ? t("countryPage.action.edit_success")
            : t("countryPage.action.add_success"),
          {
            position: "bottom-right",
            autoClose: 3000,
            closeOnClick: true,
            className: "add-success",
          }
        );
      }
    } catch (err) {
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <CountryGuideModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
      />

      <div className="px-4 py-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between mb-5">
          <h1 className="text-xl font-semibold text-gray-900">
            {id
              ? t("countryPage.update_country")
              : t("countryPage.add_country")}
          </h1>

          <button
            type="button"
            onClick={() => setIsModalOpen(true)}
            className="inline-flex items-center gap-1.5 text-sm font-medium text-blue-600 hover:text-blue-800 border border-blue-300 hover:border-blue-500 rounded-lg px-3 py-1.5 transition-colors bg-blue-50 hover:bg-blue-100"
          >
            📖 {t("countryPage.guide")}
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="overflow-hidden shadow sm:rounded-md">
            <div className="px-4 py-5 bg-white sm:p-6">
              <div className="grid grid-cols-6 gap-6">
                <div className="col-span-6 sm:col-span-3">
                  <label
                    htmlFor="name"
                    className="block text-sm font-medium text-gray-700"
                  >
                    {t("countryPage.name")} (name)
                  </label>
                  <input
                    type="text"
                    name="name"
                    id="name"
                    required
                    value={form.name}
                    onChange={(e) =>
                      setForm((prev) => ({ ...prev, name: e.target.value }))
                    }
                    className="block w-full mt-1 border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                  />
                </div>

                <div className="col-span-6 sm:col-span-3">
                  <label
                    htmlFor="code"
                    className="block text-sm font-medium text-gray-700"
                  >
                    {t("countryPage.code")} (code)
                  </label>
                  <input
                    type="text"
                    name="code"
                    id="code"
                    required
                    value={form.code}
                    onChange={(e) =>
                      setForm((prev) => ({ ...prev, code: e.target.value }))
                    }
                    className="block w-full mt-1 border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                  />
                </div>

                <div className="col-span-6 sm:col-span-3">
                  <label
                    htmlFor="phoneCode"
                    className="block text-sm font-medium text-gray-700"
                  >
                    {t("countryPage.phone_code")} (phoneCode)
                  </label>
                  <input
                    type="text"
                    name="phoneCode"
                    id="phoneCode"
                    required
                    value={form.phoneCode}
                    onChange={(e) =>
                      setForm((prev) => ({
                        ...prev,
                        phoneCode: e.target.value,
                      }))
                    }
                    className="block w-full mt-1 border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                  />
                </div>

                <div className="col-span-6 sm:col-span-3">
                  <label
                    htmlFor="digitsLength"
                    className="block text-sm font-medium text-gray-700"
                  >
                    {t("countryPage.digits_length")} (digitsLength)
                  </label>
                  <input
                    type="number"
                    name="digitsLength"
                    id="digitsLength"
                    required
                    value={form.digitsLength}
                    onChange={(e) =>
                      setForm((prev) => ({
                        ...prev,
                        digitsLength: e.target.value,
                      }))
                    }
                    className="block w-full mt-1 border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                  />
                </div>

                <div className="col-span-6 sm:col-span-3">
                  <label
                    htmlFor="phoneFormat"
                    className="block text-sm font-medium text-gray-700"
                  >
                    {t("countryPage.phone_format")} (phoneFormat)
                  </label>
                  <input
                    type="text"
                    name="phoneFormat"
                    id="phoneFormat"
                    required
                    value={form.phoneFormat}
                    onChange={(e) =>
                      setForm((prev) => ({
                        ...prev,
                        phoneFormat: e.target.value,
                      }))
                    }
                    className="block w-full mt-1 border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                  />
                </div>

                <div className="col-span-6 sm:col-span-3">
                  <label
                    htmlFor="length"
                    className="block text-sm font-medium text-gray-700"
                  >
                    {t("countryPage.length")} (length)
                  </label>
                  <input
                    type="number"
                    name="length"
                    id="length"
                    required
                    value={form.length}
                    onChange={(e) =>
                      setForm((prev) => ({ ...prev, length: e.target.value }))
                    }
                    className="block w-full mt-1 border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                  />
                </div>

                <div className="col-span-6 sm:col-span-3">
                  <label
                    htmlFor="codeSlice"
                    className="block text-sm font-medium text-gray-700"
                  >
                    {t("countryPage.code_slice")} (codeSlice)
                  </label>
                  <input
                    type="text"
                    name="codeSlice"
                    id="codeSlice"
                    required
                    value={form.codeSlice}
                    onChange={(e) =>
                      setForm((prev) => ({
                        ...prev,
                        codeSlice: e.target.value,
                      }))
                    }
                    className="block w-full mt-1 border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                  />
                </div>

                <div className="col-span-6 sm:col-span-3">
                  <label className="block text-sm font-medium text-gray-700">
                    {t("countryPage.file")} (flag Image )
                  </label>
                  <input
                    type="file"
                    onChange={(e) =>
                      setForm({ ...form, file: e.target.files[0] })
                    }
                    className="block w-full mt-1 border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                  />
                </div>

                <div className="col-span-6 sm:col-span-3">
                  <label
                    htmlFor="currencyId"
                    className="block text-sm font-medium text-gray-700"
                  >
                    {t("countryPage.currency")} (currencyId)
                  </label>
                  <select
                    id="currencyId"
                    name="currencyId"
                    value={form.currencyId || ""}
                    onChange={(e) =>
                      setForm((prev) => ({
                        ...prev,
                        currencyId: e.target.value || null,
                      }))
                    }
                    className="block w-full mt-1 border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                  >
                    <option value="">{t("countryPage.select_currency")}</option>
                    {currencies.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.name} ({c.symbol})
                      </option>
                    ))}
                  </select>
                </div>

                <div className="col-span-6 grid gap-y-4">
                  <ToggleWithLabel
                    label={t("countryPage.is_active")}
                    enabled={isActive}
                    setEnabled={handleSetIsActive}
                  />
                  <ToggleWithLabel
                    label={t("countryPage.is_default")}
                    enabled={isDefault}
                    setEnabled={handleSetIsDefault}
                  />
                </div>

                {id && <CountryProviders countryId={id} />}
              </div>
            </div>

            <div className="flex justify-end px-4 py-3 text-right bg-gray-50 sm:px-6">
              <Button type="button" to="/country" btnName={t("backward")} />
              <button
                type="submit"
                className="text-white bg-blue-700 hover:bg-blue-800 font-medium rounded-lg text-sm px-5 py-1.5 text-center dark:bg-blue-600 dark:hover:bg-blue-700 inline-flex items-center ml-3"
              >
                {t("save")}
                {isLoading ? (
                  <svg
                    role="status"
                    className="inline w-4 h-4 ml-1 text-white animate-spin"
                    viewBox="0 0 100 101"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
                      fill="#E5E7EB"
                    />
                    <path
                      d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
                      fill="currentColor"
                    />
                  </svg>
                ) : (
                  <MdOutlineDataSaverOff className="w-4 h-4 ml-1" />
                )}
              </button>
            </div>
          </div>
        </form>
      </div>
    </>
  );
};

export default withTranslation()(AddAndEditCountry);
